// @flow
import React from 'react'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import MuiThemeProvider from '@material-ui/core/styles/MuiThemeProvider'
import { SnackbarProvider } from '@modules/snackbar'
import i18n from 'i18next'

import { Router, routerHistory } from '@modules/router'
import { persistor, store } from './master/store/composer'
import theme from './master/theme'
import { asyncComponent } from './AsyncComponent'
import AppshellContainer from './appshell/AppshellContainer'
import ReviewContainer from './checkout/review/ReviewContainer'
import DeliveryContainer from './checkout/delivery/DeliveryContainer'
import PaymentContainer from './checkout/payment/PaymentContainer'

const AuthContainer = asyncComponent(() => import('./auth/AuthContainer'))
const AccountVerificationContainer = asyncComponent(() =>
  import('./auth/AccountVerificationContainer')
)
const Error404 = asyncComponent(() => import('./static/components/Error404'))
const ProductContainer = asyncComponent(() =>
  import('./product/ProductContainer')
)
const CatalogContainer = asyncComponent(() =>
  import('./catalog/CatalogContainer')
)
const RecoveryContainer = asyncComponent(() =>
  import('./auth/RecoveryContainer')
)
const ResetPasswordContainer = asyncComponent(() =>
  import('./auth/ResetPasswordContainer')
)
const StaticPageContainer = asyncComponent(() =>
  import('./static/StaticPageContainer')
)
const ThanksContainer = asyncComponent(() =>
  import('./checkout/thanks/ThanksContainer')
)
const AddAddressContainer = asyncComponent(() =>
  import('./account/personal/AddAddressContainer')
)
const OrdersContainer = asyncComponent(() =>
  import('./account/orders/OrdersContainer')
)
const OrderDetailContainer = asyncComponent(() =>
  import('./account/orders/OrderDetailContainer')
)
const PersonalContainer = asyncComponent(() =>
  import('./account/personal/PersonalContainer')
)
const EditPersonalDataContainer = asyncComponent(() =>
  import('./account/personal/EditPersonalDataContainer')
)
const PrivacyContainer = asyncComponent(() =>
  import('./account/privacy/PrivacyContainer')
)
const EditAddressContainer = asyncComponent(() =>
  import('./account/personal/EditAddressContainer')
)

const es = i18n.getFixedT('es')
const ca = i18n.getFixedT('ca')

const App = () => (
  <Provider store={store}>
    <PersistGate persistor={persistor}>
      <MuiThemeProvider theme={theme}>
        <AppshellContainer>
          <Router
            history={routerHistory}
            noMatchComponent={Error404}
            routes={[
              {
                path: ca('routes.ACCOUNT_ORDERS'),
                component: OrdersContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_PERSONAL'),
                component: PersonalContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_PRIVACY'),
                component: PrivacyContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_PERSONAL_ADDRESS_EDIT'),
                component: EditAddressContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_PERSONAL_DATA_EDIT'),
                component: EditPersonalDataContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_PERSONAL_ADDRESS_ADD'),
                component: AddAddressContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              {
                path: ca('routes.ACCOUNT_ORDER_DETAIL'),
                component: OrderDetailContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              { path: ca('routes.AUTH'), component: AuthContainer },
              {
                path: ca('routes.ACCOUNT_VERIFICATION'),
                component: AccountVerificationContainer
              },
              {
                path: ca('routes.CHECKOUT_REVIEW'),
                component: ReviewContainer
              },
              {
                path: ca('routes.CHECKOUT_REVIEW_RECOMMENDATIONS'),
                component: ReviewContainer,
                secured: true,
                redirectPath: `${ca('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: ca('routes.CHECKOUT_DELIVERY'),
                component: DeliveryContainer,
                secured: true,
                redirectPath: `${ca('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: ca('routes.CHECKOUT_PAYMENT'),
                component: PaymentContainer,
                secured: true,
                redirectPath: `${ca('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: ca('routes.CHECKOUT_THANKS'),
                component: ThanksContainer,
                secured: true,
                redirectPath: ca('routes.AUTH')
              },
              { path: ca('routes.HOME'), component: CatalogContainer },
              { path: ca('routes.PRODUCT'), component: ProductContainer },
              { path: ca('routes.CATALOG'), component: CatalogContainer },
              { path: ca('routes.RECOVERY'), component: RecoveryContainer },
              {
                path: ca('routes.RESET_PASSWORD'),
                component: ResetPasswordContainer
              },
              {
                path: ca('routes.STATIC_PAGE'),
                component: StaticPageContainer
              },
              {
                path: es('routes.ACCOUNT_ORDERS'),
                component: OrdersContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_PERSONAL'),
                component: PersonalContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_PRIVACY'),
                component: PrivacyContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_PERSONAL_ADDRESS_EDIT'),
                component: EditAddressContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_PERSONAL_DATA_EDIT'),
                component: EditPersonalDataContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_PERSONAL_ADDRESS_ADD'),
                component: AddAddressContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              {
                path: es('routes.ACCOUNT_ORDER_DETAIL'),
                component: OrderDetailContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              { path: es('routes.AUTH'), component: AuthContainer },
              {
                path: es('routes.ACCOUNT_VERIFICATION'),
                component: AccountVerificationContainer
              },
              {
                path: es('routes.CHECKOUT_REVIEW'),
                component: ReviewContainer
              },
              {
                path: es('routes.CHECKOUT_REVIEW_RECOMMENDATIONS'),
                component: ReviewContainer,
                secured: true,
                redirectPath: `${ca('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: es('routes.CHECKOUT_DELIVERY'),
                component: DeliveryContainer,
                secured: true,
                redirectPath: `${es('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: es('routes.CHECKOUT_PAYMENT'),
                component: PaymentContainer,
                secured: true,
                redirectPath: `${es('routes.AUTH')}?userComesFromCheckout=true`
              },
              {
                path: es('routes.CHECKOUT_THANKS'),
                component: ThanksContainer,
                secured: true,
                redirectPath: es('routes.AUTH')
              },
              { path: es('routes.HOME'), component: CatalogContainer },
              { path: es('routes.PRODUCT'), component: ProductContainer },
              { path: es('routes.CATALOG'), component: CatalogContainer },
              { path: es('routes.RECOVERY'), component: RecoveryContainer },
              {
                path: es('routes.RESET_PASSWORD'),
                component: ResetPasswordContainer
              },
              { path: es('routes.STATIC_PAGE'), component: StaticPageContainer }
            ]}
          />
        </AppshellContainer>
        <SnackbarProvider
          SnackbarProps={{
            autoHideDuration: 4500,
            anchorOrigin: { vertical: 'bottom', horizontal: 'right' }
          }}
        />
      </MuiThemeProvider>
    </PersistGate>
  </Provider>
)

export default App
